<script setup lang="ts">
import store from "@/store";
import {
  DIRECTION_PICKUP,
  DELIVERED_TO_TERMINAL,
  CARRIER_BOOKED_PICKUP,
  weightRegex,
  heightCodeRegex,
  loadingUnitRegex,
  containerTypeRegex,
  licensePlateRegex,
  COUNTER_CANCELLATION_NEEDED,
} from "@/utils/constants";
import DbInput from "@/components/DB-Components/DbInput.vue";
import OrderContainerTypeInput from "@/components/orders/OrderContainerTypeInput.vue";
import OrderHeightInput from "@/components/orders/OrderHeightInput.vue";
import OrderWeightInput from "@/components/orders/OrderWeightInput.vue";
import OrderLicensePlateInput from "@/components/orders/OrderLicensePlateInput.vue";
import StatusChangeButton from "@/components/StatusChangeButton.vue";
import { ref, reactive, computed, onMounted } from "vue";
import { i18n } from "@/i18n/i18n";
import router from "@/router";
const { t } = i18n.global;
const emit = defineEmits(["close", "cancel"]);

interface FormValidation {
  loading_unit_formatted: string;
  type: string;
  heightCode: string;
  loadWeight: string;
  emptyWeight: string;
  licensePlate: string;
}

const props = defineProps({
  order: {
    type: Object,
    default: () => ({
      orderId: null,
      orderPosition: null,
      primaryReference: null,
      secondaryReference: null,
      directionType: null,
      transportationStatus: null,
      loading_unit_formatted: null,
      loadUnit: {
        identifierPrefix: null,
        identifierNumber: null,
        type: null,
        lengthCode: null,
        heightCode: null,
        emptyWeight: null,
        loadWeight: null,
        totalWeight: null,
        loadingStatus: null,
        dangerousGoods: false, // Set default value for boolean properties
        waste: false,
        limitedQuantity: false,
        customsGoods: false,
      },
      train: {
        trainNumber: null,
        operatorId: null,
        operatorName: null,
        shippingDate: null,
        trainClosingTime: null,
        trainArrivalTime: null,
        trainState: null,
      },
      terminal: {
        terminalId: {
          locationCode: null,
          authority: null,
        },
        terminalName: null,
      },
      freightForwarderId: null,
      freightForwarderName: null,
      licensePlate: null,
      driverId: null,
    }),
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  index: Number,
});
const orderItem = ref(props.order);
const loadUnit = ref(props.order.loadUnit);
const showCounterCancellationNeeded = ref(false);

let formValidations = reactive({
  loading_unit_formatted: { invalid: "false", rx: loadingUnitRegex },
  type: { invalid: "false", rx: containerTypeRegex },
  heightCode: { invalid: "false", rx: heightCodeRegex },
  loadWeight: { invalid: "false", rx: weightRegex },
  emptyWeight: { invalid: "false", rx: weightRegex },
  licensePlate: { invalid: "false", rx: licensePlateRegex },
});

const isLoading = computed(() => {
  return orderItem.value.orderId != null;
});

const validateInput = (
  input: string,
  rx: RegExp,
  formValidationsKey: keyof FormValidation
) => {
  if (input) {
    formValidations[formValidationsKey].invalid = `${!rx.test(input)}`;
  } else {
    formValidations[formValidationsKey].invalid = "true";
  }
  formValidations = { ...formValidations };
};

const showError = (formValidationsKey: keyof FormValidation) => {
  let errorType = "Save";
  if (formValidationsKey === "licensePlate") {
    errorType = "LicensePlate";
  }
  return formValidations[formValidationsKey].invalid === "true"
    ? t(`order.error${errorType}`)
    : undefined;
};

const allFieldsValidated = () => {
  return Object.keys(formValidations).every(
    (key) =>
      formValidations[key as keyof typeof formValidations].invalid === "false"
  );
};

onMounted(() => {
  const formValidationKeys: (keyof FormValidation)[] = [
    "loading_unit_formatted",
    "licensePlate",
  ];
  formValidationKeys.forEach((key) =>
    validateInput(orderItem.value[key], formValidations[key].rx, key)
  );
  const nestedFormValidationKeys: (keyof FormValidation)[] = [
    "type",
    "heightCode",
    "loadWeight",
    "emptyWeight",
  ];
  nestedFormValidationKeys.forEach((key) =>
    validateInput(loadUnit.value[key], formValidations[key].rx, key)
  );
});
const save = () => {
  store.dispatch("OrderStore/updateTourOrder", {
    order: orderItem.value,
    index: props.index,
    params: {
      loading_unit_formatted: orderItem.value.loading_unit_formatted,
      loadUnit: orderItem.value.loadUnit,
      licensePlate: orderItem.value.licensePlate,
    },
  });
  close();
};

const close = () => {
  // orderItem.value = props.order;
  router.push("/tour");
};

const cancelPermission = () => {
  if (
    props.order.transportationStatus == CARRIER_BOOKED_PICKUP &&
    props.order?.directionType?.toLowerCase() == DIRECTION_PICKUP
  ) {
    showCounterCancellationNeeded.value = true;
  } else {
    cancel();
    router.push("/");
  }
};

const cancel = () => {
  store.dispatch("OrderStore/removeTour", {
    order: props.order,
    index: props.index,
  });
};

const discardOrder = () => {
  cancelPermission();
};

const cancelPickUpOrderCancellationNeeded = () => {
  let newTransportationStatus = "";
  switch (props.order.transportationStatus) {
    case CARRIER_BOOKED_PICKUP:
      newTransportationStatus = COUNTER_CANCELLATION_NEEDED;
      break;
    default:
      break;
  }

  if (newTransportationStatus) {
    store.dispatch("OrderStore/updateTourOrder", {
      order: props.order,
      index: props.index,
      params: {
        transportationStatus: newTransportationStatus,
      },
    });
  } else {
    console.error(
      "We don't know how to handle this transportationStatus yet. Please update code."
    );
  }

  showCounterCancellationNeeded.value = false;

  router.push("/tour");
};
</script>
<template>
  <div
    v-if="isLoading"
    id="order-details"
    class="bg-white min-w-full w-full z-20 overflow-auto bg-white rounded mb-5"
  >
    <div
      :class="{
        'pointer-events-none opacity-50':
          orderItem.transportationStatus === COUNTER_CANCELLATION_NEEDED,
      }"
      class="flex flex-col"
    >
      <div class="flex flex-col gap-1 p-2 mx-2 border-b">
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left min-w-[100px]"
            >{{ $t("order.loading_unit") }}:</span
          >
          <DbInput
            :disabled="true"
            v-model="orderItem.loading_unit_formatted"
            :value="orderItem.loading_unit_formatted"
            class="w-full hydrated"
            :ariainvalid="formValidations['loading_unit_formatted'].invalid"
            v-on:update:modelValue="
              () =>
                validateInput(
                  orderItem.loading_unit_formatted,
                  loadingUnitRegex,
                  'loading_unit_formatted'
                )
            "
            :error-message="showError('loading_unit_formatted')"
          ></DbInput>
        </div>
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left w-[100px] min-w-[100px]"
            >{{ $t("order.unit_type") }}:</span
          >
          <OrderContainerTypeInput
            v-if="loadUnit"
            :disabled="true"
            v-model="orderItem.loadUnit.type"
            :value="orderItem.loadUnit.type"
            :lengthRestriction="orderItem.loadUnit.lengthCode"
            class="w-full hydrated"
            :ariainvalid="
              formValidations['type'].invalid === 'true' ? true : false
            "
            v-on:change="
              () =>
                validateInput(
                  orderItem.loadUnit.type,
                  containerTypeRegex,
                  'type'
                )
            "
            :error-message="showError('type')"
          >
          </OrderContainerTypeInput>
        </div>
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left w-[100px] min-w-[100px]"
            >{{ $t("order.length") }}:</span
          >
          <DbInput
            v-model="orderItem.loadUnit.lengthCode"
            :value="orderItem.loadUnit.lengthCode"
            disabled
            class="w-full hydrated"
          ></DbInput>
        </div>
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left w-[100px] min-w-[100px]"
            >{{ $t("order.height") }}:</span
          >
          <OrderHeightInput
            v-model="orderItem.loadUnit.heightCode"
            :disabled="true"
            :ariainvalid="
              formValidations['heightCode'].invalid === 'true' ? true : false
            "
            v-on:change="
              () =>
                validateInput(
                  orderItem.loadUnit.heightCode,
                  heightCodeRegex,
                  'heightCode'
                )
            "
            :error-message="showError('heightCode')"
          ></OrderHeightInput>
        </div>
      </div>
      <div class="flex flex-col gap-1 p-2 mx-2 border-b">
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left w-[140px] min-w-[140px]"
            >{{ $t("order.load_weight") }}:</span
          >
          <OrderWeightInput
            placeholder="1.000 kg"
            v-model="orderItem.loadUnit.loadWeight"
            :value="orderItem.loadUnit.loadWeight"
            :disabled="true"
            :ariainvalid="formValidations['loadWeight'].invalid"
            v-on:update:modelValue="
              () =>
                validateInput(
                  `${orderItem.loadUnit.loadWeight}`,
                  weightRegex,
                  'loadWeight'
                )
            "
            :error-message="showError('loadWeight')"
          ></OrderWeightInput>
        </div>
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left w-[140px] min-w-[140px]"
            >{{ $t("order.net_weight") }}:</span
          >
          <OrderWeightInput
            placeholder="4.000 kg"
            v-model="orderItem.loadUnit.emptyWeight"
            :value="orderItem.loadUnit.emptyWeight"
            :disabled="true"
            :ariainvalid="formValidations['emptyWeight'].invalid"
            v-on:update:modelValue="
              () =>
                validateInput(
                  `${orderItem.loadUnit.emptyWeight}`,
                  weightRegex,
                  'emptyWeight'
                )
            "
            :error-message="showError('emptyWeight')"
          ></OrderWeightInput>
        </div>
        <div class="w-full flex items-center gap-2">
          <span class="text-lg text-left w-[140px] min-w-[140px]"
            >{{ $t("order.total_weight") }}:</span
          >
          <OrderWeightInput
            placeholder="4.000 kg"
            v-model="orderItem.loadUnit.totalWeight"
            :value="orderItem.loadUnit.totalWeight"
            :disabled="true"
          ></OrderWeightInput>
        </div>
      </div>
    </div>
    <div
      v-if="order.transportationStatus !== COUNTER_CANCELLATION_NEEDED"
      class="flex flex-col gap-6 p-2 mx-2"
    >
      <div class="w-full flex justify-between items-center gap-6">
        <db-button
          @click="close"
          disabled
          class="w-full hydrated"
          variant="secondary-solid"
          >{{ $t("order.cancel") }}</db-button
        >
        <db-button
          @click="save"
          disabled
          class="w-full hydrated"
          variant="primary"
          >{{ $t("order.save") }}</db-button
        >
      </div>
      <div class="w-full flex justify-between items-center gap-6">
        <db-button
          disabled
          @click="discardOrder"
          class="w-full text-black hydrated"
          variant="secondary-solid"
          >{{ $t("order.discard_task") }}</db-button
        >
        <StatusChangeButton
          @close="close"
          :order="orderItem"
          :index="index"
          :disabled="true"
        ></StatusChangeButton>
      </div>
    </div>
  </div>
  <div class="min-h-screen flex items-center justify-center bg-gray-100" v-else>
    <div class="text-center">
      <h1 class="text-4xl font-bold mb-4">404</h1>
      <p class="text-gray-700">{{ $t("order.pageNotFound") }}</p>
    </div>
  </div>
</template>
